import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "./ForgotPassword.css";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [stage, setStage] = useState(1); // 1: Enter email, 2: Enter code, 3: Reset password
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${BACKEND_URL}/admin/forgot-password`,
        {
          email
        }
      );

      if (response.status === 200) {
        setMessage(response.data.message);
        setError("");
        setStage(2); // Move to the next stage to enter the code
      } else {
        setError(
          response.data.message ||
            "Failed to send password reset link. Please try again."
        );
        setMessage("");
      }
    } catch (error) {
      if (error.response) {
        setError(
          error.response.data.message ||
            "Failed to send password reset link. Please try again."
        );
        setMessage("");
      } else if (error.request) {
        console.error("Request:", error.request);
      } else {
        console.error("Error message:", error.message);
      }
      console.log("SHOW ME THE RESPONSE : ", error);
      console.error("Error config:", error.config);
      setError(`${error?.response?.data?.message}`);
      setMessage("");
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const verificationCode = code.join(""); // Join the 4 digits to form the code
    try {
      const response = await axios.post(`${BACKEND_URL}/admin/verify-code`, {
        email,
        code: verificationCode
      });

      if (response.status === 200) {
        setMessage(response.data.message);
        setError("");
        setStage(3); // Move to the next stage to reset the password
      } else {
        setError(
          response.data.message ||
            "Failed to verify the code. Please try again."
        );
        setMessage("");
      }
    } catch (error) {
      if (error.response) {
        setError(
          error.response.data.message ||
            "Failed to verify the code. Please try again."
        );
        setMessage("");
      } else if (error.request) {
        console.error("Request:", error.request);
      } else {
        console.error("Error message:", error.message);
      }
      console.log("SHOW ME THE RESPONSE : ", error);
      console.error("Error config:", error.config);
      setError("Failed to verify the code. Please try again.");
      setMessage("");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/admin/reset-password`, {
        email,
        password: newPassword
      });

      if (response.status === 200) {
        setMessage(response.data.message);
        setError("");
        setStage(1); // Reset stage to start
      } else {
        setError(
          response.data.message ||
            "Failed to reset the password. Please try again."
        );
        setMessage("");
      }
    } catch (error) {
      if (error.response) {
        setError(
          error.response.data.message ||
            "Failed to reset the password. Please try again."
        );
        setMessage("");
      } else if (error.request) {
        console.error("Request:", error.request);
      } else {
        console.error("Error message:", error.message);
      }
      setError("Failed to reset the password. Please try again.");
      setMessage("");
    }
  };

  const handleChangeCode = (index, value) => {
    if (/^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Move to next input field if value is entered
      if (value && index < 3) {
        document.getElementById(`code-${index + 1}`).focus();
      }

      // Move to previous input field if value is deleted
      if (!value && index > 0) {
        document.getElementById(`code-${index - 1}`).focus();
      }
    }
  };

  return (
    <main className="forgot-password-main">
      <div className="forgot-password-container">
        <div className="forgot-password-content">
          <div className="text-center">
            <h1 className="title">Forgot password?</h1>
            <p className="subtitle">
              Remember your password?
              <a className="login-link" href="#" onClick={() => navigate("/")}>
                Login here
              </a>
            </p>
          </div>

          <div className="form-container">
            {stage === 1 && (
              <form onSubmit={handleForgotPassword}>
                <div className="input-group">
                  <label htmlFor="email" className="input-label">
                    Email address
                  </label>
                  <div className="input-field-container">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="input-field"
                      required
                      aria-describedby="email-error"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <p
                    className={`error-message ${error ? "visible" : "hidden"}`}
                    id="email-error"
                    dangerouslySetInnerHTML={{ __html: error }}
                  />
                </div>
                <button type="submit" className="submit-button">
                  Reset password
                </button>
                {message && (
                  <div
                    className="success-message"
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                )}
              </form>
            )}

            {stage === 2 && (
              <form onSubmit={handleVerifyCode}>
                <div className="input-group">
                  <label htmlFor="code" className="input-label">
                    Enter the 4-digit code sent to your email
                  </label>
                  <div className="code-container">
                    {code.map((digit, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        className="code-input"
                        id={`code-${index}`}
                        value={digit}
                        onChange={(e) =>
                          handleChangeCode(index, e.target.value)
                        }
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  <p
                    className={`error-message ${error ? "visible" : "hidden"}`}
                    id="code-error"
                    dangerouslySetInnerHTML={{ __html: error }}
                  />
                </div>
                <button type="submit" className="submit-button">
                  Verify code
                </button>
                {message && (
                  <div
                    className="success-message"
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                )}
              </form>
            )}

            {stage === 3 && (
              <form onSubmit={handleResetPassword}>
                <div className="input-group">
                  <label htmlFor="newPassword" className="input-label">
                    New password
                  </label>
                  <div className="input-field-container password-container">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      name="newPassword"
                      className="input-field"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <FontAwesomeIcon
                      icon={showNewPassword ? faEyeSlash : faEye}
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="password-toggle-icon"
                    />
                  </div>
                  <label htmlFor="confirmPassword" className="input-label">
                    Confirm new password
                  </label>
                  <div className="input-field-container password-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      className="input-field"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="password-toggle-icon"
                    />
                  </div>
                  <p
                    className={`error-message ${error ? "visible" : "hidden"}`}
                    id="password-error"
                    dangerouslySetInnerHTML={{ __html: error }}
                  />
                </div>
                <button type="submit" className="submit-button">
                  Change password
                </button>
                {message && (
                  <div
                    className="success-message"
                    dangerouslySetInnerHTML={{ __html: message }}
                  />
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ForgotPassword;
